import {Button} from '@wandb/components';
import {useGatedValue} from '@wandb/weave/common/state/hooks';
import {getPanelStacksForType} from '@wandb/weave/components/Panel2/availablePanels';
import {themes} from '@wandb/weave/components/Panel2/Editor.styles';
import type * as Panel2 from '@wandb/weave/components/Panel2/panel';
import {
  PanelComp2,
  TransactionalPanelConfigEditor,
} from '@wandb/weave/components/Panel2/PanelComp';
import {
  applyOpToOneOrMany,
  constString,
  opArtifactMembershipArtifactVersion,
  opArtifactMembershipCollection,
  opArtifactMembershipFile,
  opArtifactMembershipVersionIndex,
  opArtifactName,
  opArtifactProject,
  opArtifactVersionFileCount,
  opArtifactVersionId,
  opArtifactVersionSize,
  opDict,
  opEntityName,
  opProjectEntity,
  opProjectName,
} from '@wandb/weave/core';
import * as TypeHelpers from '@wandb/weave/core/model/helpers';
import {ALL_DIR_TYPE} from '@wandb/weave/core/model/types';
import * as LLReact from '@wandb/weave/react';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useParams} from 'react-router';
import {Dropdown} from 'semantic-ui-react';
import {ThemeProvider} from 'styled-components';

import {type PerformanceMetricsContext} from '../../util/datadog/artifacts/trackPageMetric';
import {useDataFetchTiming} from '../../util/datadog/artifacts/useDataFetchTiming';
import {AddToReportModal} from '../AddToReportModal';
import {ArtifactFileImagePreview} from '../ArtifactFileImagePreview';
import {DownloadArtifactButton} from '../ArtifactMembership/ArtifactDownload/DownloadArtifactButton';
import {type WBSlateElementType} from '../Slate/WBSlate/types';

const inputType = {
  type: 'union' as const,
  members: [
    'artifactMembership' as const,
    {type: 'list' as const, objectType: 'artifactMembership' as const},
  ],
};

// Image files in the artifact file browser get a dedicated click-to-zoom
// preview. They're matched by path extension because an artifact membership
// file's resolved type doesn't reliably carry an `image-file` wbObjectType;
// this mirrors PanelDir's extension-based classification. Add extensions here
// if more image types need the full-size preview.
// TODO: replace extension matching with a shared type/MIME-based check.
const IMAGE_FILE_EXTENSION_REGEX = /\.(png|jpe?g|gif|tiff?|bmp|webp)$/i;

export interface PanelFilesConfig {
  path?: string[];
}

type FilesProps = Panel2.PanelProps<typeof inputType, PanelFilesConfig> & {
  hideHeader?: boolean;
  isDownloadable?: boolean;
  /** When provided, emits data-fetch metrics for this panel. */
  metricsContext?: PerformanceMetricsContext;
  /** Called once when the initial file-type resolution completes. */
  onReady?: () => void;
};

export const FilesPanel: React.FC<FilesProps> = props => {
  const {
    config,
    context,
    updateConfig,
    updateContext,
    isDownloadable,
    metricsContext,
    onReady,
  } = props;
  const rawConfigPath = useMemo(() => config?.path, [config]);
  const configPath = useMemo(() => rawConfigPath ?? [], [rawConfigPath]);
  const pathString = useMemo(
    () => (Array.isArray(configPath) ? configPath.join('/') : configPath),
    [configPath]
  );

  const artifacts = props.input;
  const artifactNodes = useMemo(() => {
    return applyOpToOneOrMany(
      ({artifactMembership}) => {
        const versionNode = opArtifactMembershipArtifactVersion({
          artifactMembership,
        });
        const artifactNode = opArtifactMembershipCollection({
          artifactMembership,
        });
        const projectNode = opArtifactProject({artifact: artifactNode});

        return opDict({
          entityName: opEntityName({
            entity: opProjectEntity({project: projectNode}),
          }),
          projectName: opProjectName({project: projectNode}),
          artifactCollectionName: opArtifactName({
            artifact: artifactNode,
          }),
          versionIndex: opArtifactMembershipVersionIndex({artifactMembership}),
          versionId: opArtifactVersionId({artifactVersion: versionNode}),
          size: opArtifactVersionSize({artifactVersion: versionNode}),
          fileCount: opArtifactVersionFileCount({artifactVersion: versionNode}),
        } as any);
      },
      'artifactMembership',
      artifacts,
      {}
    );
  }, [artifacts]);
  const artifactNodesValue = LLReact.useNodeValue(artifactNodes as any);
  const isArtifactsList = TypeHelpers.isListLike(artifacts.type);

  const files = useMemo(() => {
    return applyOpToOneOrMany(
      opArtifactMembershipFile,
      'artifactMembership',
      artifacts,
      {
        path: constString(pathString),
      }
    );
  }, [artifacts, pathString]);
  const fileWithTypeRaw = LLReact.useNodeWithServerType(files);
  const fileWithType = useGatedValue(fileWithTypeRaw, ft => !ft.loading);

  const params = useParams<{reportNameAndID?: string}>();
  const showExportToReportButton = params.reportNameAndID == null;

  // We currently just store configs of our previews here, keyed by previewer
  // type. This is probably not really right. Maybe use file path?
  // TODO: better config strategy
  const [subConfigs, setSubConfigs] = useState<{[key: string]: any}>({});

  const [curPanelIdState, setCurPanelId] = useState<string | undefined>(
    undefined
  );

  const panelInputType = fileWithType.result.type;
  const isListOfDirs =
    TypeHelpers.isListLike(panelInputType) &&
    TypeHelpers.isAssignableTo(
      TypeHelpers.listObjectType(panelInputType),
      ALL_DIR_TYPE
    );
  const {curPanelId, stackIds, handler} = useMemo(() => {
    if (isListOfDirs) {
      return getPanelStacksForType(panelInputType, curPanelIdState, {
        allowedPanels: ['row.dir', 'row.obj'],
      });
    }
    return getPanelStacksForType(panelInputType, curPanelIdState);
  }, [panelInputType, curPanelIdState, isListOfDirs]);
  const isImageFile = useMemo(
    () => IMAGE_FILE_EXTENSION_REGEX.test(pathString),
    [pathString]
  );
  const finalInput = useMemo(() => {
    return fileWithType.result;
  }, [fileWithType.result]);

  const panelConfig = useMemo(() => {
    const baseConfig =
      curPanelId != null ? subConfigs[curPanelId + '-' + pathString] : {};
    return {
      ...baseConfig,
      isDownloadable: isDownloadable ?? true,
    };
  }, [curPanelId, pathString, subConfigs, isDownloadable]);

  const panelUpdateConfig = useCallback(
    (newConfig: any) => {
      if (curPanelId != null) {
        setSubConfigs(curSubConfigs => ({
          ...curSubConfigs,
          [curPanelId! + '-' + pathString]: {
            ...curSubConfigs[curPanelId! + '-' + pathString],
            ...newConfig,
          },
        }));
      }
    },
    [curPanelId, pathString]
  );

  const newPanelContext = useMemo(
    () => ({
      ...context,
      path: configPath,
    }),
    [context, configPath]
  );

  const panelUpdateContext = useCallback(
    (newContext: any) => {
      if (newContext.path != null) {
        updateConfig({path: newContext.path});
      }
      updateContext(newContext);
    },
    [updateConfig, updateContext]
  );

  const reportBlock: WBSlateElementType = useMemo(() => {
    return {
      type: 'weave-panel',
      config: {
        panelConfig: {
          panelConfig,
          exp: fileWithType.result,
          panelId: curPanelId,
        },
        height: 800,
      },
      children: [{text: ''}],
    };
  }, [panelConfig, fileWithType.result, curPanelId]);

  const isLoading = fileWithTypeRaw.loading;

  // If the resolved type is 'none', the file doesn't exist in this artifact version.
  // Redirect back to the files root so the user sees the file listing instead of a blank page.
  useEffect(() => {
    if (!isLoading && configPath.length > 0) {
      // Weave wraps types in tagged values to carry metadata (e.g. entity/project
      // context). In practice this resolves in a single unwrap, but the while loop
      // handles arbitrarily deep nesting to reach the actual value type.
      let innerType = panelInputType;
      while (TypeHelpers.isTaggedValue(innerType)) {
        innerType = TypeHelpers.taggedValueValueType(innerType);
      }
      if (innerType === 'none') {
        updateConfig({path: []});
      }
    }
  }, [isLoading, configPath, panelInputType, updateConfig]);

  useDataFetchTiming(
    metricsContext ?? null,
    isLoading,
    'artifact_files',
    0,
    onReady
  );

  return (
    <ThemeProvider theme={themes.light}>
      <div
        className="file-browser artifact-file-browser"
        style={{
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
          overflow: 'hidden',
          padding: 0,
        }}>
        {!props.hideHeader && (
          <div
            className="file-browser-path"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 16,
              position: 'sticky',
              left: '0px',
              // Otherwise files table is in front of dropdown
              zIndex: 1,
            }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                flex: '0 0 auto',
              }}>
              <div
                style={{
                  fontSize: 24,
                  opacity: 0.7,
                }}>
                &gt;&nbsp;
                {['root'].concat(configPath).map((folderName, i) => {
                  const newPath = configPath.slice(0, i);
                  return [
                    <span
                      className="file-browser-path-item"
                      style={{cursor: 'pointer'}}
                      // oxlint-disable-next-line react/no-array-index-key -- https://oxc.rs/docs/guide/usage/linter/rules/react/no-array-index-key.html
                      key={'path' + i}
                      onClick={() => updateConfig({path: newPath})}>
                      {folderName}
                    </span>,
                    i !== configPath.length ? ' / ' : undefined,
                  ];
                })}
              </div>

              {!isLoading && stackIds.length > 1 && (
                <Dropdown
                  selection
                  className="clone-report-modal__select"
                  style={{marginLeft: 24}}
                  options={stackIds.map(si => ({
                    key: si.id,
                    text: si.displayName,
                    value: si.id,
                  }))}
                  value={curPanelId}
                  onChange={(e, {value}) => setCurPanelId(value as string)}
                />
              )}
            </div>
            <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
              {showExportToReportButton &&
                curPanelId != null &&
                !window.location.pathname.includes('/reports/') && (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                    }}>
                    <AddToReportModal
                      trigger={
                        <Button size="md" variant="secondary">
                          Add to report
                        </Button>
                      }
                      reportBlock={reportBlock}
                    />
                  </div>
                )}
              {isDownloadable === true &&
                artifactNodesValue.result &&
                !isArtifactsList && (
                  <DownloadArtifactButton
                    key={artifactNodesValue.result.versionId}
                    artifactCollectionName={
                      artifactNodesValue.result.artifactCollectionName
                    }
                    entityName={artifactNodesValue.result.entityName}
                    projectName={artifactNodesValue.result.projectName}
                    versionId={artifactNodesValue.result.versionId}
                    versionIndex={artifactNodesValue.result.versionIndex}
                    artifactSize={artifactNodesValue.result.size}
                    fileCount={artifactNodesValue.result.fileCount}
                  />
                )}
            </div>
          </div>
        )}
        <>
          {handler != null && (
            <div style={{flex: '0 1 auto', overflow: 'show'}}>
              <TransactionalPanelConfigEditor
                panelSpec={handler}
                input={finalInput as any}
                config={panelConfig}
                updateConfig={panelUpdateConfig}
              />
            </div>
          )}
          <div style={{flex: '1 1 auto', overflow: 'hidden'}}>
            {isImageFile ? (
              <ArtifactFileImagePreview
                fileNode={finalInput as any}
                name={pathString}
              />
            ) : handler != null ? (
              <PanelComp2
                panelSpec={handler}
                loading={isLoading}
                input={finalInput as any}
                inputType={panelInputType}
                config={panelConfig}
                configMode={false}
                context={newPanelContext}
                updateConfig={panelUpdateConfig}
                updateContext={panelUpdateContext}
              />
            ) : (
              <div>no preview</div>
            )}
          </div>
        </>
      </div>
    </ThemeProvider>
  );
};
